import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import moment from 'moment';
import {
  FaClock,
  FaNotesMedical,
  FaSave,
  FaCheck,
  FaArrowLeft,
  FaStethoscope,
  FaClipboardList,
  FaPlus,
  FaExternalLinkAlt,
  FaPrescriptionBottleAlt,
  FaImages,
  FaFileAlt,
  FaUserFriends
} from 'react-icons/fa';
import AddDiagnosisModal from '../../components/modals/AddDiagnosisModal';
import AddAllergyModal from '../../components/modals/AddAllergyModal';
import AddMedicationModal, { Medication } from '../../components/modals/AddMedicationModal';
import PatientReferralHeaderInfo from '../../components/patients/PatientReferralHeaderInfo';
import InterestedPartiesTab from '../../components/patients/InterestedPartiesTab';

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  patient_id: string;
  date_of_birth: string;
  phone?: string;
  mobile_phone?: string;
  email?: string;
  address?: string;
  medicare_number?: string;
}

interface Appointment {
  id: string;
  patient: string;
  patient_details?: Patient;
  patient_name: string;
  provider?: string;
  provider_name?: string;
  title: string;
  start_time: string;
  end_time: string;
  status: string;
  room_name?: string;
  notes?: string;
  procedure_description?: string;
}

interface PainWorkupSheet {
  id: string;
  patient_id: string;
  consultation_id?: string;
  assessment_date: string;
  status: string;
  pain_score: number;
  primary_location: string;
  is_signed: boolean;
}

interface PatientDiagnosis {
  id: string;
  diagnosis_name?: string;
  diagnosis_icd_10?: string;
  diagnosis_date?: string;
  severity?: string;
  is_primary?: boolean;
}

interface PatientAllergy {
  id: string;
  allergy_name: string;
  reaction?: string;
  severity: string;
  is_verified?: boolean;
}

interface RecentNote {
  id: string;
  subject?: string;
  note_type?: string;
  content?: string;
  note_date: string;
  provider_name?: string;
}

interface ClinicalSummary {
  diagnoses: PatientDiagnosis[];
  allergies: PatientAllergy[];
  medications: Medication[];
  recent_notes: RecentNote[];
}

type SidePanel = 'assessments' | 'prescriptions' | 'imaging' | 'documents' | 'interestedParties' | null;

const CurrentConsult: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidePanel, setSidePanel] = useState<SidePanel>(null);

  // Clinical note state
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [historyOfPresentIllness, setHistoryOfPresentIllness] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [consultDuration, setConsultDuration] = useState<string>('00:00');
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // Consultation record backing this screen - created lazily the first time
  // it's needed so diagnoses/allergies/medications/notes added during the
  // visit can link back to it via consultation_id (all of which stays
  // optional - adding to a patient's file outside a consult works exactly
  // the same, consultation_id is just left null there).
  const [consultationId, setConsultationId] = useState<string | null>(null);

  const [painWorkupSheets, setPainWorkupSheets] = useState<PainWorkupSheet[]>([]);
  const [loadingPainWorkups, setLoadingPainWorkups] = useState(false);

  const [summary, setSummary] = useState<ClinicalSummary>({ diagnoses: [], allergies: [], medications: [], recent_notes: [] });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [isDiagnosisModalOpen, setIsDiagnosisModalOpen] = useState(false);
  const [isAllergyModalOpen, setIsAllergyModalOpen] = useState(false);
  const [isMedicationModalOpen, setIsMedicationModalOpen] = useState(false);
  const [discontinuingMedication, setDiscontinuingMedication] = useState<Medication | null>(null);

  const loadAppointment = useCallback(async () => {
    if (!token || !appointmentId) return;

    setLoading(true);
    try {
      const response = await apiCall(
        `/scheduling/appointments/${appointmentId}/`,
        { method: 'GET' },
        token
      );
      const data = await response.json();
      setAppointment(data);

      if (data.patient_details) {
        setPatient(data.patient_details);
      } else if (data.patient) {
        const patientResponse = await apiCall(
          `/patients/${data.patient}/`,
          { method: 'GET' },
          token
        );
        const patientData = await patientResponse.json();
        setPatient(patientData);
      }
    } catch (error) {
      console.error('Error loading appointment:', error);
    } finally {
      setLoading(false);
    }
  }, [token, appointmentId]);

  useEffect(() => {
    loadAppointment();
  }, [loadAppointment]);

  // Timer for consultation duration
  useEffect(() => {
    if (!appointment || appointment.status !== 'in_consult') return;

    const start = startTime || new Date();
    if (!startTime) setStartTime(start);

    const timer = setInterval(() => {
      const now = new Date();
      const diff = Math.floor((now.getTime() - start.getTime()) / 1000);
      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;
      setConsultDuration(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(timer);
  }, [appointment, startTime]);

  // Find-or-create the Consultation record for this appointment. Runs once
  // the appointment has loaded (need its provider/patient/start/end times).
  useEffect(() => {
    const resolveConsultation = async () => {
      if (!token || !appointment || !patient) return;

      try {
        const existing = await apiCall(
          `/clinical/consultations/?appointment_id=${appointment.id}`,
          { method: 'GET' },
          token
        );
        if (existing.ok) {
          const data = await existing.json();
          const list = data.results || data || [];
          if (list.length > 0) {
            setConsultationId(list[0].id);
            setChiefComplaint(list[0].chief_complaint || '');
            return;
          }
        }

        const created = await apiCall('/clinical/consultations/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patient_id: patient.id,
            provider_id: appointment.provider || null,
            appointment_id: appointment.id,
            consultation_type: 'Consultation',
            start_time: appointment.start_time,
            end_time: appointment.end_time,
            status: 'IN_PROGRESS',
            chief_complaint: '',
          }),
        }, token);
        if (created.ok) {
          const newConsultation = await created.json();
          setConsultationId(newConsultation.id);
        }
      } catch (error) {
        console.error('Error resolving consultation record:', error);
      }
    };

    resolveConsultation();
  }, [token, appointment, patient]);

  const loadClinicalSummary = useCallback(async () => {
    if (!patient?.id || !token) return;
    setLoadingSummary(true);
    try {
      const response = await apiCall(`/clinical/patients/${patient.id}/clinical-summary/`, { method: 'GET' }, token);
      if (response.ok) {
        const data = await response.json();
        setSummary({
          diagnoses: data.diagnoses || [],
          allergies: data.allergies || [],
          medications: (data.medications || []).filter((m: Medication) => m.status !== 'Discontinued'),
          recent_notes: data.recent_notes || [],
        });
      }
    } catch (error) {
      console.error('Error loading clinical summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  }, [patient?.id, token]);

  useEffect(() => {
    loadClinicalSummary();
  }, [loadClinicalSummary]);

  const loadPainWorkupSheets = useCallback(async () => {
    if (!token || !patient || !consultationId) return;

    setLoadingPainWorkups(true);
    try {
      const response = await apiCall(
        `/clinical/pain-workup/?consultation_id=${consultationId}`,
        { method: 'GET' },
        token
      );
      const data = await response.json();
      setPainWorkupSheets(data);
    } catch (error) {
      console.error('Error loading pain workup sheets:', error);
    } finally {
      setLoadingPainWorkups(false);
    }
  }, [token, patient, consultationId]);

  useEffect(() => {
    if (patient && sidePanel === 'assessments') {
      loadPainWorkupSheets();
    }
  }, [patient, sidePanel, loadPainWorkupSheets]);

  const handleCreatePainWorkup = async () => {
    if (!token || !patient) return;

    try {
      const response = await apiCall(
        '/clinical/pain-workup/',
        {
          method: 'POST',
          body: JSON.stringify({
            patient_id: patient.id,
            consultation_id: consultationId,
            status: 'DRAFT'
          })
        },
        token
      );

      if (response.ok) {
        const newWorkup = await response.json();
        navigate(`/clinicalassessment/painworkupsheet?workup_id=${newWorkup.id}&patient_id=${patient.id}&consultation_id=${consultationId}`);
      }
    } catch (error) {
      console.error('Error creating pain workup sheet:', error);
      alert('Failed to create pain workup sheet');
    }
  };

  const handleViewPainWorkup = (workupId: string) => {
    navigate(`/clinicalassessment/painworkupsheet?workup_id=${workupId}&patient_id=${patient?.id}&consultation_id=${consultationId}`);
  };

  const handleCompleteConsult = async () => {
    if (!token || !appointmentId) return;
    setIsCompleting(true);

    try {
      await apiCall(
        `/scheduling/appointments/${appointmentId}/`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: 'completed' })
        },
        token
      );

      if (consultationId) {
        await apiCall(`/clinical/consultations/${consultationId}/complete/`, { method: 'POST' }, token);
      }

      navigate('/dashboards/clinician');
    } catch (error) {
      console.error('Error completing consult:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!token || !patient) return;
    setIsSaving(true);

    try {
      if (consultationId && chiefComplaint) {
        await apiCall(`/clinical/consultations/${consultationId}/`, {
          method: 'PATCH',
          body: JSON.stringify({ chief_complaint: chiefComplaint }),
        }, token);
      }

      const noteContent = [
        historyOfPresentIllness && `History of Present Illness:\n${historyOfPresentIllness}`,
        assessment && `Assessment:\n${assessment}`,
        plan && `Plan:\n${plan}`,
        clinicalNotes && `Notes:\n${clinicalNotes}`,
      ].filter(Boolean).join('\n\n');

      if (noteContent) {
        await apiCall('/clinical/notes/', {
          method: 'POST',
          body: JSON.stringify({
            patient_id: patient.id,
            provider_id: appointment?.provider || null,
            consultation_id: consultationId,
            note_type: 'Progress Note',
            note_date: new Date().toISOString(),
            subject: chiefComplaint || 'Consultation Notes',
            content: noteContent,
            is_draft: false,
          }),
        }, token);
      }

      loadClinicalSummary();
      alert('Notes saved successfully!');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <p className="mt-4 text-lg">Loading consultation...</p>
        </div>
      </div>
    );
  }

  if (!appointment || !patient) {
    return (
      <div className="p-10 text-center">
        <p className="text-xl text-error">Appointment or patient not found</p>
        <button onClick={() => navigate('/dashboards/clinician')} className="btn btn-primary mt-4">
          Return to Dashboard
        </button>
      </div>
    );
  }

  const activeMedications = summary.medications;

  return (
    <div className="min-h-screen bg-base-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/dashboards/clinician')}
            className="btn btn-ghost gap-2"
          >
            <FaArrowLeft /> Back to Dashboard
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleSaveNotes}
              disabled={isSaving}
              className="btn btn-success gap-2"
            >
              <FaSave /> {isSaving ? 'Saving...' : 'Save Notes'}
            </button>
            <button
              onClick={handleCompleteConsult}
              disabled={isCompleting}
              className="btn btn-primary gap-2"
            >
              <FaCheck /> {isCompleting ? 'Completing...' : 'Complete Consult'}
            </button>
          </div>
        </div>

        {/* Patient Information Banner */}
        <div className="card bg-gradient-to-r from-primary to-secondary text-primary-content shadow-xl mb-4">
          <div className="card-body py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* Title & Timer */}
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3">
                    <FaStethoscope className="text-3xl" />
                    Current Consultation
                  </h1>
                </div>

                {appointment.status === 'in_consult' && (
                  <div className="flex items-center gap-2 bg-base-100/20 px-4 py-2 rounded-lg">
                    <FaClock className="text-xl" />
                    <div>
                      <p className="text-xs opacity-70">Duration</p>
                      <p className="text-xl font-mono font-bold">{consultDuration}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Patient Info Inline */}
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-sm opacity-70">Patient</p>
                  <p className="text-lg font-bold">{patient.first_name} {patient.last_name}</p>
                </div>
                <div>
                  <p className="text-sm opacity-70">MRN</p>
                  <p className="font-semibold">{patient.patient_id}</p>
                </div>
                <div>
                  <p className="text-sm opacity-70">DOB / Age</p>
                  <p className="font-semibold">
                    {moment(patient.date_of_birth).format('DD/MM/YYYY')} ({moment().diff(moment(patient.date_of_birth), 'years')}y)
                  </p>
                </div>
                {patient.phone && (
                  <div>
                    <p className="text-sm opacity-70">Phone</p>
                    <p className="font-semibold">{patient.phone}</p>
                  </div>
                )}
                <div>
                  <button
                    onClick={() => navigate(`/patients/${patient.id}`)}
                    className="btn btn-sm btn-outline text-primary-content border-primary-content hover:bg-primary-content hover:text-primary gap-2"
                  >
                    <FaExternalLinkAlt /> Full Record
                  </button>
                </div>
              </div>

              {/* Status Badge */}
              <div className="badge badge-lg badge-success gap-2 animate-pulse">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-content opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-success-content"></span>
                </span>
                IN CONSULT
              </div>
            </div>

            {/* Referring Doctor / Interested Parties / Referral Status - own
                row underneath so it doesn't compress the other fields */}
            <div className="mt-3 pt-3 border-t border-primary-content/20">
              <PatientReferralHeaderInfo
                patientId={patient.id}
                onViewInterestedParties={() => setSidePanel('interestedParties')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main layout: documentation (wide) + patient snapshot (narrow) side by side */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Clinical Documentation - always visible, no tab-switching for these core fields */}
        <div className="xl:col-span-2 space-y-6">
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title text-2xl mb-4">Clinical Documentation</h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="form-control lg:col-span-2">
                  <label className="label">
                    <span className="label-text font-semibold">Chief Complaint</span>
                  </label>
                  <input
                    type="text"
                    placeholder="What brings the patient in today?"
                    className="input input-bordered w-full"
                    value={chiefComplaint}
                    onChange={(e) => setChiefComplaint(e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">History of Present Illness</span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full h-32"
                    placeholder="Describe the history and progression of symptoms..."
                    value={historyOfPresentIllness}
                    onChange={(e) => setHistoryOfPresentIllness(e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Clinical Notes</span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full h-32"
                    placeholder="Enter detailed clinical notes..."
                    value={clinicalNotes}
                    onChange={(e) => setClinicalNotes(e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Assessment</span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full h-32"
                    placeholder="Clinical assessment and diagnosis..."
                    value={assessment}
                    onChange={(e) => setAssessment(e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Treatment Plan</span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full h-32"
                    placeholder="Treatment plan and next steps..."
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                  />
                </div>
              </div>

              {/* Secondary sections as tabs - bigger sub-features, not core fields */}
              <div className="tabs tabs-boxed mt-6 mb-4">
                <a className={`tab ${sidePanel === 'assessments' ? 'tab-active' : ''}`} onClick={() => setSidePanel(sidePanel === 'assessments' ? null : 'assessments')}>
                  <FaClipboardList className="mr-2" /> Clinical Assessments
                </a>
                <a className={`tab ${sidePanel === 'prescriptions' ? 'tab-active' : ''}`} onClick={() => setSidePanel(sidePanel === 'prescriptions' ? null : 'prescriptions')}>
                  <FaPrescriptionBottleAlt className="mr-2" /> Prescriptions
                </a>
                <a className={`tab ${sidePanel === 'imaging' ? 'tab-active' : ''}`} onClick={() => setSidePanel(sidePanel === 'imaging' ? null : 'imaging')}>
                  <FaImages className="mr-2" /> Imaging
                </a>
                <a className={`tab ${sidePanel === 'documents' ? 'tab-active' : ''}`} onClick={() => setSidePanel(sidePanel === 'documents' ? null : 'documents')}>
                  <FaFileAlt className="mr-2" /> Documents
                </a>
                <a className={`tab ${sidePanel === 'interestedParties' ? 'tab-active' : ''}`} onClick={() => setSidePanel(sidePanel === 'interestedParties' ? null : 'interestedParties')}>
                  <FaUserFriends className="mr-2" /> Interested Parties
                </a>
              </div>

              {sidePanel === 'assessments' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b border-base-300">
                    <div>
                      <h3 className="text-lg font-semibold">Clinical Assessment Forms</h3>
                      <p className="text-sm opacity-70">Create and manage detailed clinical assessments</p>
                    </div>
                    <button onClick={handleCreatePainWorkup} className="btn btn-primary gap-2">
                      <FaPlus /> Create Pain Workup Sheet
                    </button>
                  </div>

                  {loadingPainWorkups ? (
                    <div className="flex justify-center py-12">
                      <div className="loading loading-spinner loading-lg text-primary"></div>
                    </div>
                  ) : painWorkupSheets.length > 0 ? (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-base">Pain Workup Sheets for this Consultation</h4>
                      {painWorkupSheets.map((workup) => (
                        <div
                          key={workup.id}
                          className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => handleViewPainWorkup(workup.id)}
                        >
                          <div className="card-body p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <FaClipboardList className="text-primary text-xl" />
                                  <div>
                                    <h5 className="font-semibold">
                                      Pain Workup Sheet - {moment(workup.assessment_date).format('MMM D, YYYY HH:mm')}
                                    </h5>
                                    <div className="flex gap-4 mt-1 text-sm opacity-70">
                                      <span>Pain Score: <strong>{workup.pain_score}/10</strong></span>
                                      <span>Location: <strong>{workup.primary_location}</strong></span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className={`badge ${workup.is_signed ? 'badge-success' : workup.status === 'DRAFT' ? 'badge-warning' : 'badge-info'}`}>
                                  {workup.is_signed ? 'Signed' : workup.status}
                                </div>
                                <FaExternalLinkAlt className="text-primary opacity-50" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-base-200 rounded-lg">
                      <FaClipboardList className="text-6xl mx-auto mb-4 opacity-30" />
                      <p className="text-lg mb-4">No pain workup sheets for this consultation yet</p>
                      <button onClick={handleCreatePainWorkup} className="btn btn-primary gap-2">
                        <FaPlus /> Create Pain Workup Sheet
                      </button>
                    </div>
                  )}
                </div>
              )}

              {sidePanel === 'prescriptions' && (
                <div className="text-center py-12 opacity-60">
                  <FaPrescriptionBottleAlt className="text-6xl mx-auto mb-4" />
                  <p className="text-lg">Prescription management coming soon...</p>
                </div>
              )}

              {sidePanel === 'imaging' && (
                <div className="text-center py-12 opacity-60">
                  <FaImages className="text-6xl mx-auto mb-4" />
                  <p className="text-lg">Imaging viewer coming soon...</p>
                </div>
              )}

              {sidePanel === 'documents' && (
                <div className="text-center py-12 opacity-60">
                  <FaFileAlt className="text-6xl mx-auto mb-4" />
                  <p className="text-lg">Document management coming soon...</p>
                </div>
              )}

              {sidePanel === 'interestedParties' && (
                <InterestedPartiesTab patientId={patient.id} />
              )}
            </div>
          </div>
        </div>

        {/* Patient Snapshot - same data/modals as Patient Details' Overview tab */}
        <div className="space-y-6">
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-base-content flex items-center gap-2">
                  <FaNotesMedical className="text-primary" /> Active Diagnoses
                </h3>
                <button onClick={() => setIsDiagnosisModalOpen(true)} className="text-sm text-secondary hover:text-secondary-focus">
                  Add
                </button>
              </div>
              <div className="space-y-3">
                {loadingSummary ? (
                  <p className="text-sm opacity-60">Loading...</p>
                ) : summary.diagnoses.length > 0 ? (
                  summary.diagnoses.map((diagnosis) => (
                    <div key={diagnosis.id} className="border-l-4 border-blue-500 pl-3">
                      <p className="font-medium text-sm">{diagnosis.diagnosis_name}</p>
                      <p className="text-xs opacity-60">
                        {diagnosis.diagnosis_icd_10 && `ICD-10: ${diagnosis.diagnosis_icd_10} | `}
                        {diagnosis.severity && `Severity: ${diagnosis.severity}`}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm opacity-60">No active diagnoses recorded</p>
                )}
              </div>
            </div>
          </div>

          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-base-content">Allergies</h3>
                <button onClick={() => setIsAllergyModalOpen(true)} className="text-sm text-secondary hover:text-secondary-focus">
                  Add
                </button>
              </div>
              <div className="space-y-3">
                {loadingSummary ? (
                  <p className="text-sm opacity-60">Loading...</p>
                ) : summary.allergies.length > 0 ? (
                  summary.allergies.map((allergy) => (
                    <div key={allergy.id} className="border-l-4 border-red-500 pl-3">
                      <p className="font-medium text-sm">{allergy.allergy_name}</p>
                      <p className="text-xs opacity-60">{allergy.reaction} | Severity: {allergy.severity}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm opacity-60">No allergies recorded</p>
                )}
              </div>
            </div>
          </div>

          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-base-content">Medications</h3>
                <button
                  onClick={() => { setDiscontinuingMedication(null); setIsMedicationModalOpen(true); }}
                  className="text-sm text-secondary hover:text-secondary-focus"
                >
                  Add
                </button>
              </div>
              <div className="space-y-3">
                {loadingSummary ? (
                  <p className="text-sm opacity-60">Loading...</p>
                ) : activeMedications.length > 0 ? (
                  activeMedications.map((medication) => (
                    <div key={medication.id} className="p-2 bg-base-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{medication.name}</p>
                          <p className="text-xs opacity-70">{medication.dosage} {medication.frequency}</p>
                        </div>
                        <button
                          onClick={() => { setDiscontinuingMedication(medication); setIsMedicationModalOpen(true); }}
                          className="text-xs text-secondary hover:text-secondary-focus"
                        >
                          Discontinue
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm opacity-60">No active medications</p>
                )}
              </div>
            </div>
          </div>

          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h3 className="font-semibold text-base-content mb-3">Recent Clinical Notes</h3>
              <div className="space-y-3">
                {loadingSummary ? (
                  <p className="text-sm opacity-60">Loading...</p>
                ) : summary.recent_notes.length > 0 ? (
                  summary.recent_notes.map((note) => (
                    <div key={note.id} className="border-b border-base-300 pb-2">
                      <div className="flex justify-between">
                        <p className="font-medium text-sm">{note.subject || note.note_type || 'Clinical Note'}</p>
                        <p className="text-xs opacity-60">{moment(note.note_date).format('DD/MM/YYYY')}</p>
                      </div>
                      <p className="text-xs opacity-70 line-clamp-2">{note.content}</p>
                      <p className="text-xs opacity-50 mt-1">By: {note.provider_name || 'Unknown'}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm opacity-60">No clinical notes available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Diagnosis/Allergy/Medication Modals - shared with Patient Details,
          linked back to this consultation via consultationId */}
      <AddDiagnosisModal
        isOpen={isDiagnosisModalOpen}
        onClose={() => setIsDiagnosisModalOpen(false)}
        patientId={patient.id}
        patient={patient}
        consultationId={consultationId}
        onDiagnosisAdded={() => { setIsDiagnosisModalOpen(false); loadClinicalSummary(); }}
      />
      <AddAllergyModal
        isOpen={isAllergyModalOpen}
        onClose={() => setIsAllergyModalOpen(false)}
        patientId={patient.id}
        patient={patient}
        consultationId={consultationId}
        onAllergyAdded={() => { setIsAllergyModalOpen(false); loadClinicalSummary(); }}
      />
      <AddMedicationModal
        isOpen={isMedicationModalOpen}
        onClose={() => { setIsMedicationModalOpen(false); setDiscontinuingMedication(null); }}
        patientId={patient.id}
        patient={patient}
        consultationId={consultationId}
        editingMedication={discontinuingMedication}
        onMedicationAdded={() => { setIsMedicationModalOpen(false); setDiscontinuingMedication(null); loadClinicalSummary(); }}
      />
    </div>
  );
};

export default CurrentConsult;
